import { ArrowRight, LogIn, RefreshCw, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { guestSetupPath, signInPath } from "../authReturn";

interface RecoverableErrorProps {
  error: unknown;
  returnTo?: string;
  onContinueAsGuest?: () => void;
  onRetry?: () => void;
}

function errorDetails(error: unknown) {
  if (error instanceof Error)
    return {
      message: error.message,
      code: "code" in error && typeof error.code === "string" ? error.code : "",
    };
  return { message: "Something went wrong. Please try again.", code: "" };
}

export function isIdentityError(error: unknown) {
  const { code, message } = errorDetails(error);
  return (
    ["authentication_required", "invalid_guest_token"].includes(code) ||
    /sign in or continue as a guest|guest session.*expired/i.test(message)
  );
}

export function RecoverableError({
  error,
  returnTo = "/app",
  onContinueAsGuest,
  onRetry,
}: RecoverableErrorProps) {
  const { message, code } = errorDetails(error);
  const identityError = isIdentityError(error);
  const accountRequired = code === "account_required";
  const challengeError =
    code === "turnstile_failed" || /security check|verification check/i.test(message);

  return (
    <div className="inline-error actionable-error" role="alert">
      <span>
        {identityError ? "Your guest session has expired. Choose how to continue." : message}
      </span>
      <div className="actionable-error-actions">
        {identityError && (
          <>
            <Link className="button button-ghost compact" to={signInPath(returnTo)}>
              <LogIn /> Sign in
            </Link>
            {onContinueAsGuest ? (
              <button
                className="button button-primary compact"
                type="button"
                onClick={onContinueAsGuest}
              >
                <UserRound /> Continue as guest
              </button>
            ) : (
              <Link className="button button-primary compact" to={guestSetupPath(returnTo)}>
                <UserRound /> Continue as guest
              </Link>
            )}
          </>
        )}
        {accountRequired && (
          <Link className="button button-primary compact" to={signInPath(returnTo)}>
            <LogIn /> Sign in to continue
          </Link>
        )}
        {!identityError && !accountRequired && onRetry && (
          <button className="button button-ghost compact" type="button" onClick={onRetry}>
            <RefreshCw /> {challengeError ? "Try security check again" : "Try again"}
          </button>
        )}
        {!identityError && !accountRequired && !onRetry && (
          <Link className="text-link" to="/app">
            Back to dashboard <ArrowRight />
          </Link>
        )}
      </div>
    </div>
  );
}
